import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Wallet, ArrowUpRight, ArrowDownLeft, RefreshCw, Search, Plus, Ban } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function AdminCashless() {
  const [wallets, setWallets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('wallets');
  const [actionOpen, setActionOpen] = useState(false);
  const [actionType, setActionType] = useState('topup');
  const [actionForm, setActionForm] = useState({ wallet_id: '', amount: '', reason: '' });

  const load = () => Promise.all([api.get('/admin/cashless/wallets'), api.get('/admin/cashless/transactions')])
    .then(([w, t]) => { setWallets(w.data); setTransactions(t.data); }).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openAction = (type, wid) => {
    setActionType(type); setActionForm({ wallet_id: wid, amount: '', reason: '' }); setActionOpen(true);
  };

  const handleAction = async () => {
    const endpoint = actionType === 'topup' ? '/admin/cashless/manual-topup' : '/admin/cashless/refund';
    try {
      await api.post(endpoint, { ...actionForm, amount: parseFloat(actionForm.amount) });
      toast.success(actionType === 'topup' ? 'Rechargement effectue' : 'Remboursement effectue');
      setActionOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const filtered = wallets.filter(w => {
    if (!search) return true;
    const s = search.toLowerCase();
    return w.user?.name?.toLowerCase().includes(s) || w.user?.email?.toLowerCase().includes(s) || w.wallet_id.includes(s);
  });

  const txIcon = (type) => {
    if (type === 'topup') return <ArrowDownLeft className="w-3.5 h-3.5 text-[#10B981]" />;
    if (type === 'refund') return <RefreshCw className="w-3.5 h-3.5 text-[#F59E0B]" />;
    return <ArrowUpRight className="w-3.5 h-3.5 text-[#EF4444]" />;
  };

  if (loading) return <div className="flex justify-center py-20"><div className="spin-gold w-8 h-8" /></div>;

  return (
    <div className="fade-in" data-testid="admin-cashless-page">
      <h1 className="text-2xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Outfit' }}>Cashless</h1>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {[{ id: 'wallets', label: 'Portefeuilles' }, { id: 'transactions', label: 'Transactions' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`cashless-tab-${t.id}`}
            className={`px-4 py-2 rounded-full text-sm transition-colors ${tab === t.id ? 'bg-[#D4AF37]/15 text-[#D4AF37]' : 'text-[#A1A1AA] hover:text-white bg-[#121212]'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'wallets' && (
        <>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un portefeuille..."
              data-testid="cashless-search" className="pl-10 bg-[#121212] border-white/10 rounded-xl" />
          </div>
          <div className="space-y-3">
            {filtered.map(w => (
              <div key={w.wallet_id} className="bg-[#121212] border border-white/5 rounded-xl p-4 flex items-center gap-4" data-testid={`wallet-${w.wallet_id}`}>
                <div className="bg-[#D4AF37]/10 p-2 rounded-lg"><Wallet className="w-4 h-4 text-[#D4AF37]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">{w.user?.name || 'Inconnu'}</div>
                  <div className="text-[10px] text-[#A1A1AA]">{w.user?.email}</div>
                </div>
                <div className="text-right mr-2">
                  <div className="text-sm font-bold" style={{ fontFamily: 'JetBrains Mono' }}>{w.balance.toFixed(2)} EUR</div>
                  {w.nfc_card_id && <div className="text-[10px] text-[#D4AF37]">NFC lie</div>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => openAction('topup', w.wallet_id)} data-testid={`topup-wallet-${w.wallet_id}`}
                    className="h-7 text-xs text-[#10B981] hover:text-[#10B981] hover:bg-[#10B981]/10"><Plus className="w-3 h-3 mr-1" /> Recharge</Button>
                  <Button size="sm" variant="ghost" onClick={() => openAction('refund', w.wallet_id)} data-testid={`refund-wallet-${w.wallet_id}`}
                    className="h-7 text-xs text-[#F59E0B] hover:text-[#F59E0B] hover:bg-[#F59E0B]/10"><Ban className="w-3 h-3 mr-1" /> Rembourser</Button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === 'transactions' && (
        <div className="bg-[#121212] border border-white/5 rounded-xl p-5">
          {transactions.length === 0 ? (
            <p className="text-sm text-[#A1A1AA] text-center py-8">Aucune transaction</p>
          ) : (
            <div className="space-y-2">
              {transactions.map(tx => (
                <div key={tx.tx_id} className="flex items-center gap-3 py-2.5 border-b border-white/5 last:border-0" data-testid={`tx-${tx.tx_id}`}>
                  <div className="bg-white/5 p-1.5 rounded-md">{txIcon(tx.type)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{tx.description || tx.type}</div>
                    <div className="text-[10px] text-[#A1A1AA]">
                      {tx.wallet_id} - {new Date(tx.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                  <div className={`text-sm font-semibold ${tx.amount > 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`} style={{ fontFamily: 'JetBrains Mono' }}>
                    {tx.amount > 0 ? '+' : ''}{tx.amount.toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={actionOpen} onOpenChange={setActionOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-sm">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }}>{actionType === 'topup' ? 'Rechargement manuel' : 'Remboursement'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div><Label className="text-xs text-[#A1A1AA]">Montant (EUR) *</Label>
              <Input type="number" step="0.01" value={actionForm.amount}
                onChange={e => setActionForm(p => ({ ...p, amount: e.target.value }))}
                data-testid="cashless-action-amount" className="bg-[#121212] border-white/10 rounded-xl mt-1" /></div>
            <div><Label className="text-xs text-[#A1A1AA]">Raison *</Label>
              <Input value={actionForm.reason} onChange={e => setActionForm(p => ({ ...p, reason: e.target.value }))}
                data-testid="cashless-action-reason" className="bg-[#121212] border-white/10 rounded-xl mt-1" /></div>
            <Button onClick={handleAction} data-testid="cashless-action-submit"
              className="w-full bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full">Confirmer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
